import React, { useEffect, useMemo, useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import { IconButton, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { useLocation, useNavigate } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useMutation, useQuery } from "@tanstack/react-query";
import cn from "classnames";
import { useTranslate } from "../../../../../hooks/useTranslate";
import Preloader from "../../../../../components/Preloader";
import { useStudents } from "../../../../../context/students";
import useStatuses from "../../../../../context/status/useStatuses";
import { useToast } from "../../../../../context/toast";
import SummaryBox from "../../../../../components/SummaryBox";
import AgreementBox from "../../../EnrolmentAgreement/components/AgreementBox";
import axiosManager from "../../../../../services/axiosManager";
import { useGetPaymentPlans } from "../../../../../hooks/useGetPaymentPlans";
import OptionsToggle from "../../../../../components/OptionsToggle";
import { formatCurrency } from "../../../../../utils/formatCurrency";
import { isVersionUK } from "../../../../../services/versionService";
import SnackbarToast from "../../../../../components/SnackbarToast";

const currency = isVersionUK ? "GBP" : "USD";

const BillingBlock = (props) => {
    const {
        isOpen, onOpenClick, onClose,
        studentFullName,
        isRefetching,
        agreement,
        paymentPlans,
        disabled,
        paymentType, changePaymentType,
    } = props;
    const { t } = useTranslate();
    const { activeStudentId } = useStudents();
    const toast = useToast();

    const isRollingSelected = agreement === "Rolling";

    const paymentPlansOptions = useMemo(
        () => (paymentPlans || [])
            .filter((plan) => (isRollingSelected ? plan.type !== "Monthly" : true))
            .map((plan) => ({
                value: plan.type,
                title:
                    plan.type === "Monthly"
                        ? t("tuitionBilling.payMonthly")
                        : t(isRollingSelected ? "tuitionBilling.payHalfTermly" : "tuitionBilling.payInFull"),
                onePaymentSum: plan.oneOffAmount,
                content: (
                    <>
                        {plan.oneOffAmount > 0 && (
                            <>
                                <b>
                                    {formatCurrency(currency).format(plan.oneOffAmount)}
                                    {plan.instalments.length > 0 ? t("tuitionBilling.month") : ""}
                                </b>
                                {t(plan.instalments.length > 0 ? "tuitionBilling.forMonth" : "tuitionBilling.oneOffPayment", { num: plan.instalments.length })}
                                {`${(plan.instalments.length > 12 && plan.deposit !== 0) ? ` + ${t("tuitionBilling.refundableDepositDue")} ` : ""}`}
                            </>
                        )}
                        {(plan.instalments.length > 12 && plan.deposit !== 0) && (
                            <>
                                <b>{formatCurrency(currency).format(plan.deposit)}</b>
                                {t("tuitionBilling.refundableDepositDueText")}
                            </>
                        )}
                    </>
                ),
            })),
        [paymentPlans, isRollingSelected],
    );
    const selectedPlan = paymentPlansOptions.find((plan) => plan.value === paymentType);

    const {
        mutateAsync: setPaymentPlan,
        isPending: isSettingPaymentPlan,
        error: errorSetPaymentPlan,
        reset: resetPaymentPlan,
    } = useMutation({
        mutationFn: (planName) => axiosManager.patch$(`/parent/api/students/${activeStudentId}/PaymentPlans/${planName}`),
        onError: () => toast.error(t("common.errorMessage")),
    });

    const onUpdateType = (value) => {
        setPaymentPlan(value);
        changePaymentType(value);
        onClose();
    };

    useEffect(() => {
        if (activeStudentId) changePaymentType(undefined);
    }, [activeStudentId]);

    return (
        <SummaryBox isFormBox showBorder={isOpen}>
            <Preloader
                isLoading={isRefetching || isSettingPaymentPlan}
                overflow
                showInitiallyOverflow
            >
                <div className="flex xs:flex-column align-center justify-between gap-4">
                    <div>
                        <Typography variant="h4" className="text-primary">
                            {t("tuitionBilling.paymentPlans")}
                        </Typography>
                        <Typography variant="body1" className="mt-2">
                            {t("tuitionBilling.pleaseSelectPaymentPlan", { studentFullName })}
                        </Typography>
                    </div>
                    {!isOpen && (
                        <div className="flex items-center gap-4">
                            <div
                                className={cn("billing-block__selected", { "billing-block__selected-disabled": disabled })}
                            >
                                <Typography variant="body1">{t("tuitionBilling.selectedPlan")}</Typography>
                                <Typography variant="h4" className="text-primary">
                                    {selectedPlan?.title || "-"}
                                </Typography>
                            </div>
                            <IconButton
                                aria-label="edit"
                                className="billing-block__edit-btn"
                                disableRipple
                                onClick={onOpenClick}
                                disabled={disabled}
                            >
                                <EditOutlinedIcon />
                            </IconButton>
                        </div>
                    )}
                </div>
                {isOpen && (
                    <div className="mt-12 flex flex-wrap align-stretch gap-8">
                        {paymentPlansOptions.map((item) => (
                            <AgreementBox
                                isRecommended={item.value === "Full"}
                                disabled={isSettingPaymentPlan}
                                title={item.title}
                                isSelected={paymentType === item.value}
                                onClick={() => onUpdateType(item.value)}
                            >
                                {item.content}
                            </AgreementBox>
                        ))}
                    </div>
                )}
            </Preloader>
            <SnackbarToast
                open={!!errorSetPaymentPlan}
                isError={!!errorSetPaymentPlan}
                onClose={() => resetPaymentPlan()}
            >
                {`${t("tuitionBilling.errorProceedMessage")} (status ${(errorSetPaymentPlan)?.response?.status})`}
            </SnackbarToast>
        </SummaryBox>
    );
};

export default BillingBlock;
